import { useEffect, useState } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import type { AnnouncementPosition } from '@chirpback/protocol';
import type { HostBridge } from '../../../../host.js';
import type { Palette } from '../../../../_theme/palette.js';
import { backdropAnim, drawerAnim } from '../../../../motion/keyframes.js';

interface DrawerShellProps {
  palette: Palette;
  position?: AnnouncementPosition;
  onDismiss: () => void;
  children: ReactNode;
  width?: number;
  bridge?: HostBridge;
}

const MIN_WIDTH = 620;
const DEFAULT_WIDTH = 720;

export function DrawerShell({
  palette,
  position,
  onDismiss,
  children,
  width = DEFAULT_WIDTH,
  bridge,
}: DrawerShellProps): JSX.Element {
  const side = position === 'left' ? 'left' : 'right';
  const isMobile = useIsNarrowViewport(720);
  const clampedWidth = Math.max(MIN_WIDTH, width);
  const panelWidth = isMobile ? undefined : clampedWidth;

  // Iframe goes fullscreen so the blur backdrop covers the host page and click-outside works.
  useEffect(() => {
    if (!bridge) return;
    bridge.send('chirp:surface:placement', { layout: 'drawer', position: side });
  }, [bridge, side]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onDismiss}
      style={backdropStyle(palette, side)}
    >
      <aside
        onClick={(event) => event.stopPropagation()}
        style={panelStyle(palette, side, panelWidth)}
      >
        {children}
      </aside>
    </div>
  );
}

function useIsNarrowViewport(breakpoint: number): boolean {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (): void => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return narrow;
}

function backdropStyle(palette: Palette, side: 'left' | 'right'): CSSProperties {
  return {
    position: 'fixed',
    inset: 0,
    // Light dim only — backdrop-filter blur can't reach host content from
    // inside a cross-origin iframe (browser privacy restriction). The 0.3
    // opacity scrim keeps the host page visible (light, dark, branded — all
    // show through) while giving the drawer panel readable contrast.
    background: 'rgba(0, 0, 0, 0.3)',
    display: 'flex',
    justifyContent: side === 'right' ? 'flex-end' : 'flex-start',
    fontFamily: palette.font,
    animation: backdropAnim(),
  };
}

function panelStyle(palette: Palette, side: 'left' | 'right', width: number | undefined): CSSProperties {
  return {
    width: width ?? '100%',
    maxWidth: '100vw',
    height: '100%',
    background: palette.background,
    color: palette.text,
    boxShadow:
      side === 'right'
        ? '-24px 0 48px rgba(15, 23, 42, 0.24), -8px 0 16px rgba(15, 23, 42, 0.12)'
        : '24px 0 48px rgba(15, 23, 42, 0.24), 8px 0 16px rgba(15, 23, 42, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    animation: drawerAnim(side),
    fontFamily: palette.font,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  };
}
